// MonthlyReportTable.jsx
import React, { useState } from "react";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  IconButton,
  Collapse,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

const MonthlyReportTable = ({ reportData, units, loading, error }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});

  // Map unitId → name for display
  const unitMap =
    units?.reduce((acc, u) => {
      acc[u.id] = u.name || u.id;
      return acc;
    }, {}) || {};

  const filteredReportData = reportData ? Object.entries(reportData) : [];

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = React.useMemo(() => {
    if (!filteredReportData) return [];
    return [...filteredReportData].sort(([aId, aReport], [bId, bReport]) => {
      let aValue, bValue;
      switch (orderBy) {
        case "Unit Name":
          aValue = unitMap[aId] || aId;
          bValue = unitMap[bId] || bId;
          break;
        case "Year":
          aValue = aReport.year;
          bValue = bReport.year;
          break;
        case "Month":
          aValue = aReport.month;
          bValue = bReport.month;
          break;
        case "Stop(Hrs)":
          aValue = aReport.down;
          bValue = bReport.down;
          break;
        case "Run(Hrs)":
          aValue = aReport.up;
          bValue = bReport.up;
          break;
        case "Customer(Hrs)":
          aValue = aReport.customer;
          bValue = bReport.customer;
          break;
        case "PRC(Hrs)":
          aValue = aReport.precision;
          bValue = bReport.precision;
          break;
        case "Shutdowns":
          aValue = aReport.shutDowns;
          bValue = bReport.shutDowns;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredReportData, order, orderBy]);

  const paginatedUnits = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="red">{error}</Typography>;

  const fieldKeyMap = {
    Month: "month",
    "Stop(Hrs)": "down",
    "Run(Hrs)": "up",
    "Customer(Hrs)": "customer",
    "PRC(Hrs)": "precision",
    Shutdowns: "shutDowns",
  };
  return (
    <Box>
      <Typography fontWeight="bold" sx={{ fontSize: 18, mb: 1, px: 2 }}>
        Monthly Runtime Report
      </Typography>
      <TableContainer sx={{ maxHeight: "60vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
              <TableCell
                sx={{
                  display: { xs: "table-cell", md: "none" },
                  textAlign: "center",
                }}
              >
                Details
              </TableCell>
              {[
                "Unit Name",
                "Year",
                "Month",
                "Stop(Hrs)",
                "Run(Hrs)",
                "Customer(Hrs)",
                "PRC(Hrs)",
                "Shutdowns",
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    display: {
                      xs:
                        head === "Unit Name" || head === "Year"
                          ? "table-cell"
                          : "none",
                      md: "table-cell",
                    },
                  }}
                >
                  <TableSortLabel
                    active={orderBy === head}
                    direction={orderBy === head ? order : "asc"}
                    onClick={() => handleRequestSort(head)}
                  >
                    {head}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUnits.length > 0 ? (
              paginatedUnits.map(([unitId, report], index) => (
                <React.Fragment key={unitId}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                      "&:hover": { backgroundColor: "#4a4d54" },
                    }}
                  >
                    {/* Expand button mobile */}
                    <TableCell
                      sx={{
                        display: { xs: "table-cell", md: "none" },
                        textAlign: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpansion(unitId)}
                        sx={{ color: "white" }}
                      >
                        {expandedRows[unitId] ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>

                    <TableCell>{report.unitName}</TableCell>
                    <TableCell>{report.year}</TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {new Date(report.year, report.month - 1).toLocaleString(
                        "default",
                        { month: "long" }
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {report.down}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {report.up}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {report.customer}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {report.precision}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {report.shutDowns}
                    </TableCell>
                  </TableRow>

                  {/* Mobile collapse */}
                  <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                    <TableCell
                      colSpan={3}
                      sx={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        backgroundColor:
                          index % 2 === 0 ? "#2f3136" : "#3a3c43",
                      }}
                    >
                      <Collapse
                        in={expandedRows[unitId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        {Object.entries(fieldKeyMap).map(([field, key]) => (
                          <Box
                            key={field}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#999" }}
                            >
                              {field}:
                            </Typography>
                            <Typography variant="body2" sx={{ color: "white" }}>
                              {field === "Month"
                                ? new Date(
                                    report.year,
                                    report.month - 1
                                  ).toLocaleString("default", { month: "long" })
                                : report[key]}
                            </Typography>
                          </Box>
                        ))}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                  No units found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredReportData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
    </Box>
  );
};

export default MonthlyReportTable;
